import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { FlexCol, PrimaryButton, H3Text, FlexRow, BodyTextSmall } from '@minecraft.atoms';
import { Loading } from '@minecraft.loader';
import { getEnvironment } from '@minecraft.environment';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { AppType } from '../common/types';
import useTranslation from '../I18N/useTranslation';
import { captureExceptionEvent } from '../Sentry/Sentry';
import { StepValuesType, UPLOAD_STEPS } from './constants';
import { MediaUploaderContext, FileTypeWithMeta, MediaUploaderProps } from './types';
import { useUploadWidget } from './useUploadWidget';
import { MediaUploaderDnD } from './MediaUploaderDnD';
import { MediaUploaderModal } from './MediaUploaderModal';
import { MediaUploaderPreview } from './MediaUploaderPreview';

const MEDIA_TYPES = Object.values(MediaTypeCodeEnum);
const FILE_TYPES_LINK_STEPS: StepValuesType[] = [UPLOAD_STEPS.SELECT_FILES, UPLOAD_STEPS.REVIEW_FILES];
const HIDDEN_FOOTER_STEPS: StepValuesType[] = [UPLOAD_STEPS.SELECT_FILES, UPLOAD_STEPS.FINALIZE_UPLOAD];

const INITIAL_CONTEXT: MediaUploaderContext = {
  modalOpen: false,
  uploadStep: UPLOAD_STEPS.SELECT_FILES,
  filesToPreview: [],
  filesToCleanup: new Set(),
  filesToUpload: [],
  isSubmitting: false,
  errMsg: null,
};

export const MediaUploader = ({
  artistId,
  draftUpload = false,
  maxFiles,
  mediaType: mediaTypeProp,
  onCloseUploader,
  onUploadCallback,
  openModalButtonText,
  renderChildren,
  stageName,
  availableCreditsByType,
  entitlementsByType,
  openModalButtonProps = {},
}: MediaUploaderProps) => {
  // draftUpload is `true` on Media Requests and Submission Requested Media
  // which means they upload without checking, or consuming credits
  // we're doing this to unify the types of the mediaType param
  // since they are actually the same type but typescript
  // doesn't recognize that properly
  const mediaType = mediaTypeProp as MediaTypeCodeEnum;
  const { t } = useTranslation();
  const currentApp = getEnvironment().AUDIENCE as AppType;
  const isRepsApp = currentApp === AppType.Reps;
  const isArtistMediaUpload = draftUpload === false && artistId;

  const [
    {
      errMsg,
      failedCloudinaryUploads,
      filesToCleanup,
      filesToPreview,
      filesToUpload,
      hasClosed,
      hasOpened,
      isSubmitting,
      modalOpen,
      succeededMediaUploads,
      uploadStep,
    },
    setContext,
  ] = useState<MediaUploaderContext>({ ...INITIAL_CONTEXT });
  const { showMessage } = useGlobalNotification();
  const updateContext = useCallback((newCtx: Partial<MediaUploaderContext>) => {
    setContext((prevCtx) => {
      const updatedCtx = { ...prevCtx, ...newCtx };

      if (newCtx.modalOpen === true && prevCtx.modalOpen === false) {
        updatedCtx.failedCloudinaryUploads = [];
        updatedCtx.succeededCloudinaryUploads = [];
        updatedCtx.succeededMediaUploads = [];
        updatedCtx.hasClosed = false;
        updatedCtx.hasOpened = true;
      }

      if (newCtx.filesToPreview && newCtx.filesToPreview.length > 0) {
        newCtx.filesToPreview.forEach((f) => {
          if (f.videoFileSrc) {
            updatedCtx.filesToCleanup.add(f.videoFileSrc);
          }

          if (f.fileThumbnailSrc) {
            updatedCtx.filesToCleanup.add(f.fileThumbnailSrc);
          }
        });
      }

      return updatedCtx;
    });
  }, []);

  useEffect(() => {
    if (hasOpened && modalOpen === false && !hasClosed && typeof onCloseUploader === 'function') {
      onCloseUploader(succeededMediaUploads ?? [], failedCloudinaryUploads ?? []);
      filesToCleanup.forEach((f) => URL.revokeObjectURL(f));
      filesToCleanup.clear();
      updateContext({ hasClosed: true, filesToCleanup });
    }
  }, [
    updateContext,
    modalOpen,
    hasClosed,
    hasOpened,
    onCloseUploader,
    succeededMediaUploads,
    failedCloudinaryUploads,
    filesToCleanup,
  ]);

  const toggleModal = useCallback(() => {
    updateContext({
      ...INITIAL_CONTEXT,
      modalOpen: !modalOpen,
      uploadStep: UPLOAD_STEPS.SELECT_FILES,
    });
  }, [modalOpen, updateContext]);

  const onFilesSelected = useCallback(
    ({ selectedFiles }: { selectedFiles: FileTypeWithMeta[] }) => {
      updateContext({
        filesToPreview: selectedFiles,
        uploadStep: UPLOAD_STEPS.REVIEW_FILES,
        filesToUpload: selectedFiles.map((f) => f.originalFile),
      });
    },
    [updateContext]
  );

  const { initializeCloudinaryWidgetSilently } = useUploadWidget({
    artistId: draftUpload === false && artistId ? artistId : undefined,
    maxFiles,
    mediaType,
    onFinalizeStart: (succeeded: cloudinary.CFile[], failed: cloudinary.CFile[]) => {
      updateContext({
        uploadStep: UPLOAD_STEPS.FINALIZE_UPLOAD,
        succeededCloudinaryUploads: succeeded,
        failedCloudinaryUploads: failed,
      });
    },
    onFinalizeUploadError: ({ errMsg: errorMessage, failedMedia }) => {
      // report failure reasons to sentry for tracking
      const failureOutput = failedMedia.reduce((accum, f, idx) => {
        return {
          ...accum,
          [`${idx}_filename`]: f.name,
          [`${idx}_failure_reason`]: f.statusText,
          [`${idx}_size`]: f.size,
          [`${idx}_type`]: f.type,
        };
      }, {} as Record<string, string>);

      captureExceptionEvent(new Error('handled finalize media upload failure'), {
        ...failureOutput,
        errorMessage,
        location: 'MediaUploader - onFinalizeUploadError',
      });

      updateContext({
        errMsg: errorMessage,
        isSubmitting: false,
        uploadStep: UPLOAD_STEPS.REVIEW_FILES,
        filesToPreview: filesToPreview.map((f) => {
          const hasFailed = failedMedia.find((fa) => fa.name === f.originalFile.name);

          return { ...f, uploadError: hasFailed?.statusText };
        }),
      });
    },
    onInitializeUploadError: (errorMsg: string) => {
      updateContext({
        errMsg: errorMsg,
        isSubmitting: false,
        uploadStep: UPLOAD_STEPS.REVIEW_FILES,
      });
    },
    onSuccessfulUpload: async ({ finalizedMedia, failedMedia }) => {
      const successUploads = finalizedMedia ?? [];
      await onUploadCallback?.(successUploads, failedMedia ?? []);
      if (failedMedia.length > 0) {
        // report failure reasons to sentry for tracking
        const failureOutput = failedMedia.reduce((accum, f, idx) => {
          return {
            ...accum,
            [`${idx}_filename`]: f.name,
            [`${idx}_failure_reason`]: f.statusText,
            [`${idx}_size`]: f.size,
            [`${idx}_type`]: f.type,
          };
        }, {} as Record<string, string>);

        captureExceptionEvent(new Error('handled cloudinary upload failure'), {
          ...failureOutput,
          location: 'MediaUploader - onSuccessfulUpload',
        });

        updateContext({
          errMsg: t('common:media.utilization.notAllUploaded'),
          isSubmitting: false,
          uploadStep: UPLOAD_STEPS.REVIEW_FILES,
          filesToPreview: filesToPreview.map((f) => {
            const hasFailed = failedMedia.find((fa) => fa.name === f.originalFile.name);

            return { ...f, uploadError: hasFailed?.statusText, uploadSuccess: !hasFailed };
          }),
          succeededMediaUploads: successUploads,
        });
      } else {
        showMessage({ message: t('common:toast.mediaUploadedSuccess'), color: 'success' });
        updateContext({
          ...INITIAL_CONTEXT,
          modalOpen: false,
          uploadStep: UPLOAD_STEPS.SELECT_FILES,
          succeededMediaUploads: successUploads,
        });
      }
    },
    uploadWidgetOptions: {},
  });

  const onModalSubmitBtnClick = useCallback(() => {
    updateContext({ isSubmitting: true, errMsg: null, uploadStep: UPLOAD_STEPS.UPLOAD_WIDGET });

    const selectedFilesToUpload = filesToPreview
      // Filter the files to upload based on the actual selected files (filesToUpload = selected files)
      .filter((previewFile) =>
        filesToUpload.some(
          (fileToUpload) => fileToUpload.name === previewFile.originalFile.name && !previewFile.uploadSuccess
        )
      )
      .map((f) => ({
        originalFile: f.originalFile,
        width: f.width,
        height: f.height,
      }));

    return initializeCloudinaryWidgetSilently(selectedFilesToUpload);
  }, [filesToUpload, filesToPreview, initializeCloudinaryWidgetSilently, updateContext]);

  const hasAnyFailedFiles = useMemo(() => filesToPreview.some((file) => file.uploadError), [filesToPreview]);
  const submitDisabled = useMemo(() => {
    return hasAnyFailedFiles || filesToUpload?.length === 0 || isSubmitting;
  }, [filesToUpload, hasAnyFailedFiles, isSubmitting]);
  const creditRemaining = useMemo(() => availableCreditsByType?.[mediaType], [availableCreditsByType, mediaType]);
  const isUnlimited = !Number.isFinite(entitlementsByType?.[mediaType]);

  const creditWarningMsg = useMemo(() => {
    if (!isArtistMediaUpload || isRepsApp || isUnlimited || creditRemaining - (filesToUpload?.length ?? 0) >= 0) {
      return '';
    }

    return creditRemaining <= 0
      ? t('common:media.utilization.overCredits')
      : t('common:media.utilization.overCreditsCount', { count: creditRemaining });
  }, [isArtistMediaUpload, isRepsApp, isUnlimited, creditRemaining, filesToUpload, t]);

  return (
    <>
      {uploadStep !== UPLOAD_STEPS.UPLOAD_WIDGET && MEDIA_TYPES.includes(mediaType) && (
        // when doing the upload, the cloudinary widget displays to the user
        // and we hide our modal
        <MediaUploaderModal
          isModalOpen={modalOpen}
          toggleModal={toggleModal}
          isFooterHidden={HIDDEN_FOOTER_STEPS.includes(uploadStep)}
          isHeaderHidden={false}
          submitDisabled={submitDisabled}
          handleSubmitClick={onModalSubmitBtnClick}
        >
          {stageName && isRepsApp && (
            <FlexRow className="cn_mb-6" data-testid="reps-customize-media-header">
              <H3Text>
                {t('reps:customizeSubmission.requestedSubmissionMedia', {
                  stageName,
                })}
              </H3Text>
            </FlexRow>
          )}
          {FILE_TYPES_LINK_STEPS.includes(uploadStep) && (
            <FlexCol
              borderPlacement="all"
              borderSize={0.08}
              className="cn_mb-4 cn_atom_p-4"
              wrap="nowrap"
              alignItems="center"
              justifyContent="center"
            >
              <a
                href="https://support.castingnetworks.com/hc/en-us/articles/22862430327181-Talent-Acceptable-Media-File-Types"
                target="_blank"
                rel="noopener noreferrer"
              >
                <BodyTextSmall fontWeight="medium" color="link" align="center">
                  {t('common:mediaStorage.acceptedTypesLink')}
                </BodyTextSmall>
              </a>
            </FlexCol>
          )}
          {uploadStep === UPLOAD_STEPS.REVIEW_FILES && (
            <MediaUploaderPreview
              currentMediaType={mediaType}
              errMsg={errMsg}
              filesToPreview={filesToPreview}
              filesToUpload={filesToUpload}
              onFilesSelected={(files) => updateContext({ filesToUpload: files })}
              creditWarningMsg={creditWarningMsg}
            />
          )}
          {uploadStep === UPLOAD_STEPS.SELECT_FILES && (
            <MediaUploaderDnD onFilesSelected={onFilesSelected} maxFiles={maxFiles} currentMediaType={mediaType} />
          )}
          {uploadStep === UPLOAD_STEPS.FINALIZE_UPLOAD && (
            <FlexCol className="cn_w-full cn_my-8" alignItems="center" justifyContent="center">
              <Loading />
              <p className="cn_mt-8">{t('common:media.utilization.finalizingUpload')}</p>
            </FlexCol>
          )}
        </MediaUploaderModal>
      )}
      {/* To call modal from Dropdown or other custom element */}
      {typeof renderChildren === 'function' && renderChildren({ toggleModal })}
      {MEDIA_TYPES.includes(mediaType) && typeof renderChildren !== 'function' && (
        <PrimaryButton
          label={openModalButtonText || t('common:cloudinaryUploader.defaultButton')}
          isSmall
          {...openModalButtonProps}
          type="button"
          onClick={toggleModal}
        />
      )}
    </>
  );
};
